import React, { useState } from "react";

import { Redirect } from "react-router";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";

import { useAuth, useTealium } from "@hubcapp/context";

import { Area } from "../types";
import {
  Discount,
  FranchiseType,
  ProductCategory,
  Region,
} from "@hubcapp/types";
import { getDefaultFranchiseProductCategory } from "@hubcapp/utils";
import { selectSelectedFranchise } from "@hubcapp/state";

import { AddNewAreaStateProviderProps } from "./types";
import { AreaPrice, District as UIDistrict } from "../types";
import { validateAreaPricing } from "../helpers";

import {
  mapAreaStateToCreateAreaCoverageMutation,
  transformRegionToInitialArea,
} from "./helpers";

import { useSnackbar } from "notistack";
import AddNewArea from "./AddNewArea";
import useMutationCreateArea from "./hooks/useMutationCreateAreaCoverage";

const AddNewAreaStateProvider: React.FC<AddNewAreaStateProviderProps> = ({
  startingStage = 1,
}) => {
  const {
    instructor: { id: instructorId },
  } = useAuth();
  const history = useHistory();
  const selectedFranchise = useSelector(selectSelectedFranchise);
  const [stage, setStage] = useState<number>(startingStage);
  const [area, setArea] = useState<Area | undefined>();
  const [discard, setDiscard] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { trackEvent } = useTealium();
  if (!selectedFranchise) {
    return <Redirect to="/settings/areas-and-prices/" />;
  }

  const defaultProductCategory = selectedFranchise?.proposition?.productCategories?.find(
    category =>
      category.type === getDefaultFranchiseProductCategory(selectedFranchise),
  ) as ProductCategory;

  const onMutationComplete = () => {
    setSubmitting(false);
    trackEvent({
      eventCategory: "Areas & prices – add an area",
      eventAction: "Button Click",
      eventLabel: "Save",
    });
    history.push(`/settings/areas-and-prices/`);
  };

  const [createAreaCoverage] = useMutationCreateArea({
    onCompleted: onMutationComplete,
    onError: () => {
      setSubmitting(false);
      enqueueSnackbar("Error saving area", {
        variant: "error",
        autoHideDuration: 2000,
      });
    },
  });

  const setInitialRegion = (region: Region | null) => {
    if (!selectedFranchise || !region) return;
    setArea(transformRegionToInitialArea(region, defaultProductCategory.type));

    if (area?.id === "") {
      setArea(undefined);
      setStage(1);
    } else {
      setStage(2);
    }
  };

  const handleDistrictsChange = (districts: UIDistrict[]) => {
    if (area) {
      setArea({
        ...area,
        districts,
      });
    }
  };

  const handleDiscountsChange = (discounts: Discount[]) => {
    if (area) {
      setArea({
        ...area,
        discounts,
      });
    }
  };

  const handlePriceChange = (updatedAreaPrice: AreaPrice) => {
    if (!area || !area.pricing) return;
    setArea({
      ...area,
      pricing: area.pricing.map((areaPrice: AreaPrice) => {
        if (areaPrice.id === updatedAreaPrice.id) {
          return validateAreaPricing(updatedAreaPrice);
        }

        return areaPrice;
      }),
    });
  };

  const handleDiscard = () => {
    trackEvent({
      eventCategory: "Areas & prices - Add Area",
      eventAction: "Button Click",
      eventLabel: "Discard",
    });
    setArea(undefined);
    setStage(1);
    setDiscard(!discard);
  };

  const handleSubmit = () => {
    trackEvent({
      eventCategory: "Areas & prices - Add Area",
      eventAction: "Button Click",
      eventLabel: "Save Your New Area",
    });
    if (!area || submitting) return;

    if (area.pricing?.some((areaPrice: AreaPrice) => areaPrice.error)) {
      enqueueSnackbar("Information missing", { variant: "error" });
      return;
    }

    setSubmitting(true);

    const createAreaCoverageVariables = mapAreaStateToCreateAreaCoverageMutation(
      area,
      instructorId,
      selectedFranchise.id,
    );

    createAreaCoverage({
      variables: createAreaCoverageVariables,
    });
  };

  return (
    <AddNewArea
      {...area}
      areaPrices={area?.pricing}
      loading={false}
      onDiscountsChange={handleDiscountsChange}
      onPriceChange={handlePriceChange}
      canEditDistricts={true}
      onDistrictsChange={handleDistrictsChange}
      stage={stage}
      canEditPrices={
        selectedFranchise.proposition.type !== FranchiseType.Motability
      }
      setStage={next => setStage(next)}
      setInitialRegion={setInitialRegion}
      onDiscard={handleDiscard}
      discard={discard}
      onSubmit={handleSubmit}
      submitting={submitting}
      propositionId={selectedFranchise.proposition.id}
      productCategoryId={defaultProductCategory?.id}
      productCategoryType={defaultProductCategory?.type}
    />
  );
};

export default AddNewAreaStateProvider;
